import { useChangeBookDate } from '@/components/ReportForm/ApReportFormInputInvoiceBookDateField/hooks';
import { InputBase, LabelProps } from '@/components/ReportForm/InputBase';
import { InputBaseType } from '@/components/ReportForm/InputBase/types';
import type { AppliedPayeeValues } from '@/components/ReportForm/InstantPayeeSwitcher/hooks/useChangePayeeValue';
import { ChangeBookDateDialog } from '@/components/ReportForm/Modals/ChangeXXX';
import { useTranslation } from '@/i18n';
import {
  FormDatepicker,
  FormDatepickerProps,
} from '@moneyforward/ap-frontend-components';
import { memo, useMemo } from 'react';
import { FieldValues } from 'react-hook-form';
import { useGetPopupContainer } from '../Container/PopupContainer';

export type Props<TFieldValues extends FieldValues> =
  InputBaseType<TFieldValues> &
    FormDatepickerProps<TFieldValues> & {
      onAfterUpdateBookDateRelatedInfo?: (applied: AppliedPayeeValues) => void;
    };

const InnerApReportFormInputInvoiceBookDateField = <
  TFieldValues extends FieldValues
>({
  control,
  name,
  inputId,
  label,
  tooltip,
  hidden,
  caption,
  required = false,
  onAfterUpdateBookDateRelatedInfo,
  ...rest
}: Props<TFieldValues>) => {
  const { t } = useTranslation();
  const labelProps: LabelProps = useMemo(
    () => ({ required, children: label, tooltip }),
    [required, label, tooltip]
  );
  const rules: { required: string | boolean } = useMemo(
    () => ({ required: required ? t('required') : false }),
    [required, t]
  );
  const getPopupContainer = useGetPopupContainer();
  const datepickerProps: FormDatepickerProps<TFieldValues>['datepickerProps'] =
    useMemo(
      () => ({
        placeholder: t('ap-report-form-book-date-input-placeholder'),
        inputReadOnly: false,
        getPopupContainer,
      }),
      [t, getPopupContainer]
    );

  const { onChangeAfter } = rest;
  const { open, onWrapChangeAfter, onDialogClick, onClose } =
    useChangeBookDate<TFieldValues>({
      control,
      name,
      onChangeAfter,
      onAfterUpdateBookDateRelatedInfo,
    });
  return (
    <>
      <InputBase labelProps={labelProps} description={caption} hidden={hidden}>
        <FormDatepicker<TFieldValues>
          control={control}
          name={name}
          inputId={inputId}
          single
          rules={rules}
          {...rest}
          onChangeAfter={onWrapChangeAfter}
          datepickerProps={datepickerProps}
        />
      </InputBase>

      <ChangeBookDateDialog
        key={`change-book-date-${open}`}
        open={open}
        onClose={onClose}
        onClick={onDialogClick}
      />
    </>
  );
};

InnerApReportFormInputInvoiceBookDateField.displayName =
  'InnerApReportFormInputInvoiceBookDateField';

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const ApReportFormInputInvoiceBookDateField = memo((props) => (
  <InnerApReportFormInputInvoiceBookDateField {...props} />
)) as typeof InnerApReportFormInputInvoiceBookDateField;
ApReportFormInputInvoiceBookDateField.displayName =
  'ApReportFormInputInvoiceBookDateField';
